"use client";

import React, { useState } from "react";
import Link from "next/link";
import { ArrowRight, Globe } from "lucide-react";
import { useNavigation } from "@/components/navigation-provider";
import { useSiteSettings } from "@/components/site-settings-provider";
import { subscribeNewsletter } from "@/lib/api";
import { useRecaptchaV3 } from "@/hooks/use-recaptcha-v3";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import type { NavigationMenuNode } from "@/lib/api";

/* Simple SVG social icons (brand icons removed from lucide-react v1) */
const LinkedinIcon = () => (
 <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
);
const TwitterIcon = () => (
 <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
);
const GithubIcon = () => (
 <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/></svg>
);
const InstagramIcon = () => (
 <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
);
const FacebookIcon = () => (
 <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073C24 5.404 18.627 0 12 0S0 5.404 0 12.073c0 6.019 4.388 11.009 10.125 11.927v-8.437H7.078v-3.49h3.047V9.414c0-3.018 1.792-4.686 4.533-4.686 1.312 0 2.686.235 2.686.235v2.963H15.83c-1.49 0-1.955.931-1.955 1.887v2.26h3.328l-.532 3.49h-2.796V24C19.612 23.082 24 18.092 24 12.073z"/></svg>
);
const YoutubeIcon = () => (
 <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a2.997 2.997 0 00-2.11-2.12C19.516 3.56 12 3.56 12 3.56s-7.516 0-9.388.506a2.997 2.997 0 00-2.11 2.12A31.1 31.1 0 000 12a31.1 31.1 0 00.502 5.814 2.997 2.997 0 002.11 2.12c1.872.506 9.388.506 9.388.506s7.516 0 9.388-.506a2.997 2.997 0 002.11-2.12A31.1 31.1 0 0024 12a31.1 31.1 0 00-.502-5.814zM9.546 15.568V8.432L15.818 12l-6.272 3.568z"/></svg>
);
const WhatsAppIcon = () => (
 <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.52 3.48A11.86 11.86 0 0012.06 0C5.5 0 .14 5.35.14 11.92c0 2.1.55 4.15 1.6 5.95L0 24l6.31-1.65a11.9 11.9 0 005.75 1.47h.01c6.56 0 11.92-5.35 11.92-11.92 0-3.18-1.24-6.18-3.49-8.42zm-8.46 18.3h-.01a9.88 9.88 0 01-5.03-1.38l-.36-.21-3.75.98 1-3.66-.24-.38a9.87 9.87 0 01-1.52-5.24c0-5.46 4.45-9.91 9.92-9.91 2.65 0 5.14 1.03 7.01 2.91a9.86 9.86 0 012.9 7c0 5.47-4.45 9.91-9.92 9.91zm5.43-7.41c-.3-.15-1.77-.87-2.05-.97-.27-.1-.47-.15-.67.15-.19.3-.76.97-.93 1.16-.17.2-.35.22-.64.08-.3-.15-1.25-.46-2.39-1.47-.88-.78-1.48-1.75-1.65-2.05-.17-.3-.02-.46.13-.6.13-.12.3-.35.44-.52.15-.18.2-.3.3-.5.1-.2.05-.38-.02-.53-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.5h-.57c-.2 0-.53.08-.8.38-.27.3-1.03 1-1.03 2.44 0 1.43 1.05 2.82 1.2 3.02.15.2 2.06 3.14 4.98 4.4.7.3 1.26.49 1.69.63.7.22 1.33.19 1.83.12.56-.08 1.77-.72 2.02-1.41.25-.7.25-1.29.17-1.42-.07-.12-.27-.2-.57-.35z"/></svg>
);
const TelegramIcon = () => (
 <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zm5.89 8.17l-1.97 9.29c-.15.66-.54.82-1.1.51l-3.05-2.25-1.47 1.41c-.16.16-.3.3-.62.3l.22-3.11 5.66-5.11c.25-.22-.05-.35-.38-.13L8.2 13.48l-2.99-.94c-.65-.2-.66-.65.14-.97l11.68-4.5c.54-.2 1.01.13.84 1.1z"/></svg>
);
const PinterestIcon = () => (
 <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C5.373 0 0 5.373 0 12c0 4.99 3.049 9.266 7.388 11.094-.102-.943-.194-2.39.041-3.42.213-.93 1.37-5.92 1.37-5.92s-.35-.7-.35-1.74c0-1.63.95-2.85 2.13-2.85 1 0 1.49.75 1.49 1.65 0 1-.64 2.5-.97 3.89-.28 1.16.58 2.11 1.73 2.11 2.08 0 3.68-2.19 3.68-5.35 0-2.8-2.01-4.76-4.88-4.76-3.32 0-5.26 2.49-5.26 5.07 0 1 .39 2.08.88 2.66.1.12.11.23.08.35-.09.38-.29 1.16-.33 1.31-.05.21-.17.25-.39.15-1.45-.68-2.36-2.82-2.36-4.54 0-3.69 2.68-7.08 7.74-7.08 4.06 0 7.22 2.89 7.22 6.76 0 4.03-2.54 7.27-6.07 7.27-1.18 0-2.29-.61-2.67-1.34l-.72 2.75c-.26.99-.97 2.23-1.44 2.99A11.99 11.99 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/></svg>
);
const DiscordIcon = () => (
 <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.317 4.369A19.791 19.791 0 0015.558 3c-.206.37-.444.87-.608 1.26a18.27 18.27 0 00-5.9 0 12.64 12.64 0 00-.617-1.26 19.736 19.736 0 00-4.76 1.37C.533 9.046-.32 13.58.099 18.057a19.944 19.944 0 005.993 3.043 14.315 14.315 0 001.284-2.114 12.99 12.99 0 01-2.02-.968c.17-.124.336-.254.497-.389 3.9 1.83 8.14 1.83 11.994 0 .163.135.329.265.498.389a12.94 12.94 0 01-2.024.97 14.23 14.23 0 001.284 2.113 19.902 19.902 0 005.995-3.044c.492-5.177-.84-9.67-3.283-13.688zM8.02 15.33c-1.182 0-2.154-1.085-2.154-2.419 0-1.333.951-2.418 2.154-2.418 1.212 0 2.174 1.095 2.154 2.418 0 1.334-.952 2.419-2.154 2.419zm7.96 0c-1.182 0-2.154-1.085-2.154-2.419 0-1.333.951-2.418 2.154-2.418 1.212 0 2.174 1.095 2.154 2.418 0 1.334-.942 2.419-2.154 2.419z"/></svg>
);
const BehanceIcon = () => (
 <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M9.37 11.06c.96-.47 1.45-1.29 1.45-2.46 0-2.1-1.58-3.16-4.75-3.16H0V18.7h6.29c3.44 0 5.16-1.22 5.16-3.67 0-1.79-.69-3.11-2.08-3.97zM3.15 7.71h2.63c1.09 0 1.64.4 1.64 1.2 0 .84-.55 1.26-1.64 1.26H3.15V7.71zm2.89 8.72H3.15v-3.23h2.89c1.23 0 1.85.53 1.85 1.59 0 1.09-.62 1.64-1.85 1.64zm9.43-9.08c-3.59 0-5.39 1.95-5.39 5.86 0 3.82 1.76 5.73 5.28 5.73 2.78 0 4.52-1.21 5.21-3.62h-3.01c-.18.58-.83.87-1.95.87-1.68 0-2.58-.89-2.69-2.67h7.8v-.92c0-3.51-1.75-5.25-5.25-5.25zm-2.48 4.47c.17-1.53 1.01-2.29 2.53-2.29 1.46 0 2.25.76 2.37 2.29h-4.9zM13.19 5.1h4.76v1.16h-4.76z"/></svg>
);
const DribbbleIcon = () => (
 <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0a12 12 0 100 24 12 12 0 000-24zm7.93 5.54A9.95 9.95 0 0121.99 11c-1.31-.25-2.59-.35-3.86-.29-.17-.43-.35-.85-.54-1.26 1.14-.47 2.26-1.11 3.34-1.91zM12 2.01c2.13 0 4.1.67 5.73 1.81-.96.73-1.97 1.29-3.03 1.69-1.08-1.98-2.24-3.27-2.58-3.63-.04-.04-.08-.08-.12-.12zm-2.28.27c.31.33 1.42 1.56 2.49 3.46-2.84.76-5.33.79-6.42.77A10 10 0 019.72 2.28zM4.62 7.91c1.21.02 4.08-.05 8.55-.94.16.33.31.67.46 1.02-.08.02-.17.04-.26.06C8.78 9.45 6.34 13.36 5.84 14.19A9.94 9.94 0 014.62 7.91zm2.68 8.05c.41-.68 2.5-3.98 7.06-5.53.02-.01.04-.01.06-.02.3.76.55 1.53.74 2.31-.12.03-.24.06-.36.09-2.96.96-4.49 3.58-4.84 4.24A10.02 10.02 0 017.3 15.96zm4.82 5.82c.29-.57 1.49-2.74 4.02-3.62.1-.03.2-.06.3-.09.25 1.31.39 2.63.41 3.96A10 10 0 0112.12 21.78zm6.65-1.71c-.05-1.16-.19-2.34-.42-3.51 1.13-.18 2.31-.16 3.53.08a10.04 10.04 0 01-3.11 3.43zm3.78-5.14c-1.46-.29-2.89-.31-4.24-.08-.21-.86-.48-1.71-.8-2.55 1.19-.04 2.39.08 3.62.35-.05.8-.25 1.57-.58 2.28z"/></svg>
);

const socialIconMap: Record<string, React.FC> = {
 Linkedin: LinkedinIcon,
 Twitter: TwitterIcon,
 Github: GithubIcon,
 Instagram: InstagramIcon,
 Facebook: FacebookIcon,
 Youtube: YoutubeIcon,
 WhatsApp: WhatsAppIcon,
 Telegram: TelegramIcon,
 Pinterest: PinterestIcon,
 Discord: DiscordIcon,
 Behance: BehanceIcon,
 Dribbble: DribbbleIcon,
};

type FooterColumn = {
 title: string;
 links: Array<{ name: string; href: string }>;
};

function isVisibleOnDevice(value: unknown): boolean {
 if (typeof value === "boolean") return value;
 if (typeof value === "number") return value !== 0;
 if (typeof value === "string") {
 const normalized = value.trim().toLowerCase();
 if (normalized === "false" || normalized === "0") return false;
 if (normalized === "true" || normalized === "1") return true;
 }

 return true;
}

function filterMenuItemsForDevice(items: NavigationMenuNode[], device: "desktop" | "mobile"): NavigationMenuNode[] {
 return items
 .filter((item) => (device === "desktop" ? isVisibleOnDevice(item.showOnDesktop) : isVisibleOnDevice(item.showOnMobile)))
 .map((item) => ({
 ...item,
 children: filterMenuItemsForDevice(item.children || [], device),
 }));
}

function mapFooterColumns(items: NavigationMenuNode[]): FooterColumn[] {
 return items.map((item) => {
 const title = item.title;
 const baseLinks = item.children.length > 0
 ? item.children.map((child) => ({ name: child.title, href: child.url }))
 : [{ name: item.title, href: item.url }];

 if (title.toLowerCase() === "features") {
 const cap = 10;
 const limited = baseLinks.slice(0, cap);
 const hasMoreLink = limited.some((link) => link.href === "/features");
 if (baseLinks.length > cap && !hasMoreLink) {
 limited.push({ name: "More Features", href: "/features" });
 }
 return { title, links: limited };
 }

 return { title, links: baseLinks };
 });
}

function getLegalLinks(items: NavigationMenuNode[]): Array<{ name: string; href: string }> {
 const legalSection = items.find((item) => item.title.toLowerCase() === "legal");
 if (!legalSection) {
 return [];
 }

 return legalSection.children.length > 0 ? legalSection.children.map((child) => ({ name: child.title, href: child.url })) : [{ name: legalSection.title, href: legalSection.url }];
}

export default function Footer() {
 const [email, setEmail] = useState("");
 const [subscribed, setSubscribed] = useState(false);
 const { enabled: captchaEnabled, execute: executeCaptcha } = useRecaptchaV3();

 const nav = useNavigation();
 const { settings } = useSiteSettings();
 const footerLogo = settings.branding.footerLogo || settings.branding.logoDark || settings.branding.logoLight;
 const brandText = settings.branding.textLogo || settings.general.siteName;
 const footerCta = settings.notifications.footerCta;

 const handleSubscribe = async (e: React.FormEvent) => {
 e.preventDefault();
 if (!email) return;
 try {
 const captchaToken = captchaEnabled ? await executeCaptcha("newsletter_subscribe") : undefined;
 await subscribeNewsletter(email, captchaToken || undefined);
 setSubscribed(true);
 setEmail("");
 } catch {
 // silently fail
 }
 };

 const { footerLinks, social } = nav;
 const socialLinks = settings.general.socialLinks.length > 0 ? settings.general.socialLinks : social;
 const managedFooterMenu = nav.footerMenu || [];
 const desktopManagedFooter = managedFooterMenu.length > 0 ? filterMenuItemsForDevice(managedFooterMenu, "desktop") : [];
 const mobileManagedFooter = managedFooterMenu.length > 0 ? filterMenuItemsForDevice(managedFooterMenu, "mobile") : [];
 const desktopLegalLinks = desktopManagedFooter.length > 0 ? getLegalLinks(desktopManagedFooter) : (footerLinks.legal || []);
 const mobileLegalLinks = mobileManagedFooter.length > 0 ? getLegalLinks(mobileManagedFooter) : (footerLinks.legal || []);
 const fallbackColumns: FooterColumn[] = [
 { title: "Company", links: footerLinks.company.length ? footerLinks.company : [{ name: "Contact", href: "/contact" }] },
 { title: "Services", links: footerLinks.services.length ? footerLinks.services : [{ name: "Services", href: "/services" }] },
 { title: "Products", links: footerLinks.products.length ? footerLinks.products : [{ name: "Products", href: "/products" }] },
 { title: "Resources", links: footerLinks.resources.length ? footerLinks.resources : [{ name: "Resources", href: "/resources" }] },
 { title: "Legal", links: footerLinks.legal },
 ];
 const desktopColumns = desktopManagedFooter.length > 0 ? mapFooterColumns(desktopManagedFooter) : fallbackColumns;
 const mobileColumns = mobileManagedFooter.length > 0 ? mapFooterColumns(mobileManagedFooter) : fallbackColumns;
 const quickLinks = [
 { label: "Features", href: "/features" },
 { label: "Solutions", href: "/use-cases" },
 { label: "Integrations", href: "/integrations" },
 { label: "Insights", href: "/resources/insights" },
 ];

 return (
 <footer className="bg-gray-950 text-gray-300">
 {footerCta.enabled ? (
 <div className="relative overflow-hidden border-b border-gray-800 bg-[linear-gradient(120deg,rgba(17,24,39,0.96),rgba(3,7,18,0.98),rgba(15,23,42,0.96))]">
 <div className="pointer-events-none absolute inset-0 footer-cta-aurora" />
 <div className="pointer-events-none absolute -left-24 top-0 h-64 w-64 rounded-full bg-cyan-400/20 blur-3xl footer-cta-float" />
 <div className="pointer-events-none absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-fuchsia-500/20 blur-3xl footer-cta-float-reverse" />
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
 <div className="flex flex-col gap-6 rounded-3xl border border-white/10 bg-white/5 px-6 py-8 shadow-[0_24px_80px_rgba(2,6,23,0.35)] md:flex-row md:items-center md:justify-between md:px-8">
 <div className="max-w-3xl">
 <span className="inline-flex rounded-full border border-sky-400/30 bg-sky-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-sky-200">{footerCta.badge}</span>
 <h3 className="mt-4 text-2xl font-semibold text-white md:text-3xl">{footerCta.title}</h3>
 <p className="mt-3 text-sm leading-6 text-gray-300 md:text-base">{footerCta.description}</p>
 </div>
 <div className="flex flex-col gap-3 sm:flex-row">
 <Link href={footerCta.primaryHref} className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-cyan-400 via-blue-500 to-fuchsia-500 bg-[length:220%_220%] px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-blue-500/45 footer-cta-button">{footerCta.primaryLabel}</Link>
 <Link href={footerCta.secondaryHref} className="inline-flex items-center justify-center rounded-full border border-white/20 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10">{footerCta.secondaryLabel}</Link>
 </div>
 </div>
 </div>
 </div>
 ) : null}
 {/* Newsletter Section */}
 <div className="border-b border-gray-800">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
 <div className="flex flex-col md:flex-row items-center justify-between gap-6">
 <div>
 <h3 className="text-xl font-semibold text-white">Stay updated</h3>
 <p className="text-sm text-gray-400 mt-1">Get the latest insights and updates from {settings.general.siteName}.</p>
 </div>
 {subscribed ? (
 <p className="text-green-400 text-sm font-medium">Thanks for subscribing!</p>
 ) : (
 <form onSubmit={handleSubscribe} className="flex gap-2 w-full md:w-auto">
 <Input
 type="email"
 placeholder="Enter your email"
 value={email}
 onChange={(e) => setEmail(e.target.value)}
 required
 className="bg-gray-900 border-gray-700 text-white placeholder:text-muted-foreground w-full md:w-72"
 />
 <Button type="submit" variant="gradient" size="default">
 Subscribe <ArrowRight className="w-4 h-4 ml-1" />
 </Button>
 </form>
 )}
 </div>
 </div>
 </div>

 {/* Quick Links */}
 <div className="border-b border-gray-800 bg-gray-900/50">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
 <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
 <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">Explore NotifyStudio</p>
 <div className="flex flex-wrap items-center gap-3 sm:gap-4">
 {quickLinks.map((item) => (
 <Link
 key={item.href}
 href={item.href}
 className="rounded-full border border-gray-700 px-3 py-1.5 text-sm text-gray-300 transition-colors hover:border-gray-500 hover:text-white"
 >
 {item.label}
 </Link>
 ))}
 </div>
 </div>
 </div>
 </div>

 {/* Links Section */}
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
 <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
 {/* Brand Column */}
 <div className="col-span-2 md:col-span-3 lg:col-span-1">
 <Link href="/" className="mb-4 inline-flex items-center">
 {footerLogo ? (
 <img src={footerLogo} alt={brandText} className="h-10 w-auto object-contain no-transition" />
 ) : (
 <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-violet-600">
 <span className="text-lg font-bold text-white">i</span>
 </div>
 )}
 </Link>
 <p className="text-sm text-gray-400 mb-4">
 {settings.general.footerDescription}
 </p>
 <div className="flex gap-3">
 {socialLinks.map((s) => {
 const Icon = socialIconMap[s.icon] || GithubIcon;
 return (
 <a
 key={s.name}
 href={s.href}
 target="_blank"
 rel="noopener noreferrer"
 className="w-9 h-9 rounded-lg bg-gray-800 flex items-center justify-center hover:bg-gray-700 transition-colors"
 aria-label={s.name}
 >
 <Icon />
 </a>
 );
 })}
 </div>
 </div>

 {/* Link Columns */}
 <div className="hidden md:contents">
 {desktopColumns.map((col) => (
 <div key={col.title}>
 <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">{col.title}</h4>
 <ul className="space-y-2.5">
 {col.links.map((link, i) => (
 <li key={`${col.title}-${link.name}-${link.href}-${i}`}>
 <Link href={link.href} className="text-sm text-gray-400 hover:text-white transition-colors">
 {link.name}
 </Link>
 </li>
 ))}
 </ul>
 </div>
 ))}
 </div>
 <div className="col-span-2 grid grid-cols-2 gap-8 md:hidden">
 {mobileColumns.map((col) => (
 <div key={`mobile-${col.title}`}>
 <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">{col.title}</h4>
 <ul className="space-y-2.5">
 {col.links.map((link, i) => (
 <li key={`mobile-${col.title}-${link.name}-${link.href}-${i}`}>
 <Link href={link.href} className="text-sm text-gray-400 hover:text-white transition-colors">
 {link.name}
 </Link>
 </li>
 ))}
 </ul>
 </div>
 ))}
 </div>
 </div>
 </div>

 {/* Bottom Bar */}
 <div className="border-t border-gray-800">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
 <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
 <p className="text-sm text-muted-foreground">
 &copy; {new Date().getFullYear()} {settings.general.copyrightText}
 </p>
 <div className="hidden sm:flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
 {desktopLegalLinks.map((link) => (
 <Link key={link.href} href={link.href} className="hover:text-white transition-colors">{link.name}</Link>
 ))}
 </div>
 <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground sm:hidden">
 {mobileLegalLinks.map((link) => (
 <Link key={`mobile-${link.href}`} href={link.href} className="hover:text-white transition-colors">{link.name}</Link>
 ))}
 </div>
 </div>
 </div>
 </div>
 <style jsx>{`
 .footer-cta-aurora {
	 background: radial-gradient(circle at 8% 18%, rgba(56, 189, 248, 0.24), transparent 42%), radial-gradient(circle at 92% 6%, rgba(217, 70, 239, 0.2), transparent 36%), radial-gradient(circle at 74% 94%, rgba(45, 212, 191, 0.18), transparent 40%);
 }
 .footer-cta-button {
	 animation: footerCtaShift 8s ease-in-out infinite;
 }
 .footer-cta-float {
	 animation: footerCtaFloat 8s ease-in-out infinite;
 }
 .footer-cta-float-reverse {
	 animation: footerCtaFloat 9s ease-in-out infinite reverse;
 }
 @keyframes footerCtaShift {
	 0% { background-position: 0% 50%; }
	 50% { background-position: 100% 50%; }
	 100% { background-position: 0% 50%; }
 }
 @keyframes footerCtaFloat {
	 0% { transform: translateY(0) translateX(0); }
	 50% { transform: translateY(-12px) translateX(10px); }
	 100% { transform: translateY(0) translateX(0); }
 }
 `}</style>
 </footer>
 );
}
